<template>
  <div v-if="formData" data-testid="prospects-list-form">
    <FieldWrapper
      for="list-name-input"
      :label="$t('ProspectsCatalog.createListModal.listNameLabel') + ' *'"
      :error="getValidationErrors(v$.listName)"
    >
      <template #default="{ id }">
        <InputField
          :id="id"
          v-model="formData.listName"
          data-testid="prospects-form-list-name"
          :state="getValidationState(v$.listName).state"
        />
      </template>
    </FieldWrapper>

    <FieldWrapper v-if="props.event?.slug && props.event?.name" for="event-name-input" label="Event" class="mt-6">
      <template #default="{ id }">
        <SelectField :id="id" :model-value="props.event.name" disabled data-testid="prospects-form-event">
          <option :key="props.event?.slug" :value="props.event.name">
            {{ props.event.name }}
          </option>
        </SelectField>
      </template>
    </FieldWrapper>

    <FieldWrapper
      for="contact-reason-input"
      class="mt-6"
      :label="t('ProspectsCatalog.createListModal.contactReasonLabel') + ' *'"
      :error="getValidationErrors(v$.contactReason)"
    >
      <template #default="{ id }">
        <SelectField
          :id="id"
          v-model="formData.contactReason"
          :disabled="!hasMultipleContactReasons || isSubmitted"
          data-testid="prospects-form-contact-reason"
        >
          <option v-for="(value, key) in contactReasons" :key="`select_${key}`" :value="key">{{ value }}</option>
        </SelectField>
      </template>
    </FieldWrapper>

    <p class="my-5">{{ t('ProspectsCatalog.createListModal.description') }}</p>

    <FieldWrapper
      class="mt-6"
      for="contact-name-input"
      :error="getValidationErrors(v$.contactName)"
      :label="t('ProspectsCatalog.createListModal.contactNameLabel') + ' *'"
    >
      <template #default="{ id }">
        <InputField
          :id="id"
          v-model="formData.contactName"
          data-testid="prospects-form-contact-name"
          :state="getValidationState(v$.contactName).state"
          :disabled="isSubmitted"
        >
          <template v-if="isSubmitted" #append>
            <SvgIcon :path="mdiLock" :size="24" />
          </template>
        </InputField>
      </template>
    </FieldWrapper>

    <FieldWrapper
      class="mt-6"
      for="contact-email-input"
      :error="getValidationErrors(v$.contactEmail)"
      :label="t('ProspectsCatalog.createListModal.contactEmailLabel') + ' *'"
    >
      <template #default="{ id }">
        <InputField
          :id="id"
          v-model="formData.contactEmail"
          :disabled="isSubmitted"
          :state="getValidationState(v$.contactEmail).state"
          type="email"
          data-testid="prospects-form-contact-email"
        >
          <template v-if="isSubmitted" #append>
            <SvgIcon :path="mdiLock" :size="24" />
          </template>
        </InputField>
      </template>
    </FieldWrapper>
  </div>
</template>

<script setup lang="ts">
import { mdiLock } from '@mdi/js'
import { FieldWrapper, InputField, SelectField } from '@ramp106/omrjs-core-ui'
import { useVuelidate } from '@vuelidate/core'
import { computed, reactive, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { getValidationErrors, getValidationState } from '@/helpers/validationHelper'
import { required, email } from '@/services/validators'

const { t } = useI18n()

export type ProspectListType = {
  id?: string
  title?: string
  contactName?: string | null
  contactEmail?: string | null
  contactReason?: string | null
  submittedAt?: string | null
}

export type EventType = {
  id: string
  name?: string | null
  slug?: string | null
}

const props = defineProps<{
  prospectList: ProspectListType
  event: EventType
}>()

const isCreating = computed(() => !props.prospectList.id)
const isEditing = computed(() => props.prospectList.id)
const isSubmitted = computed(() => !!props.prospectList.submittedAt)

const formData = reactive({
  listName: '',
  contactName: '',
  contactEmail: '',
  contactReason: '',
})

const rules = {
  listName: { required },
  contactName: { required },
  contactEmail: { required, email },
  contactReason: { required },
}

const v$ = useVuelidate(rules, formData)

onMounted(() => initializeFormData())

watch(
  () => props.prospectList,
  () => initializeFormData(),
)

function initializeFormData() {
  if (props.prospectList) {
    formData.listName = props.prospectList.title || ''
    formData.contactName = props.prospectList.contactName || ''
    formData.contactEmail = props.prospectList.contactEmail || ''
    formData.contactReason = props.prospectList.contactReason || ''
  }

  // Set default contact reason for new lists
  if (isCreating.value && !formData.contactReason) {
    formData.contactReason = isDmcEvent.value ? 'invitation' : 'ticket_invitation'
  }
}

const emit = defineEmits<{
  (e: 'submit', value: { listName: string; contactName: string; contactEmail: string; contactReason: string }): void
}>()

const isDmcEvent = computed(() => props.event?.slug?.startsWith('dmc'))

const contactReasons = computed(() => {
  if (isDmcEvent.value) {
    return { invitation: t('ProspectsCatalog.createListModal.contactReasons.Invitation') }
  } else {
    const reasons: Record<string, string> = {
      ticket_invitation: t('ProspectsCatalog.createListModal.contactReasons.TicketInvitation'),
    }

    // Add make_an_appointment option only when editing, not a DMC event, and the list already has this value
    if (isEditing.value && props.prospectList.contactReason === 'make_an_appointment') {
      reasons.make_an_appointment = t('ProspectsCatalog.createListModal.contactReasons.MakeAnAppointment')
    }

    return reasons
  }
})

const hasMultipleContactReasons = computed(() => Object.keys(contactReasons.value).length > 1)

function submit() {
  v$.value.$validate()
  if (!v$.value.$error) {
    emit('submit', {
      listName: formData.listName,
      contactName: formData.contactName,
      contactEmail: formData.contactEmail,
      contactReason: formData.contactReason,
    })
  }
}

defineExpose({
  submit,
})
</script>
